import { cn } from '@/lib/cn';
import type { LedgerOutcome } from './projection';

/**
 * THE RUN LEDGER — client surface
 * ===============================
 *
 * The device that makes a conversation legible: every run the platform carried
 * out for a client appears as one block in the thread, timed, with its stages
 * underneath and a glyph that says how it ended.
 *
 * Under Soft Pastel the client's ledger is a *block*, not the hairline rail the
 * operator apps draw. The rail is an instrument: it puts the run's shape on the
 * left edge of the screen and expects the reader to read it as a chart. A
 * client does not want a chart of their own request — they want a sentence
 * about it, and the stages only when they ask. So the stages collapse, and the
 * default reading is one line: what is happening, and how long it has taken.
 *
 * The data is unchanged: the same neutral projection in `projection.ts`, from
 * the same run events. Only the drawing is different, and it stays in this
 * directory for the same reason it always did — the thread renderer is
 * duplicated per app by design, so each app's ledger lives with its app.
 */

export const OUTCOME_LABEL: Record<LedgerOutcome, string> = {
  running: 'in progress',
  succeeded: 'finished',
  failed: 'stopped',
  expired: 'timed out',
  cancelled: 'cancelled',
};

/**
 * The run's glyph, in the white disc at the head of a run block.
 *
 * One shape per outcome. A running glyph pulses — it is the only moving thing
 * in the thread, and it is moving because the work is.
 */
export function RunGlyph({ outcome, className }: { outcome: LedgerOutcome; className?: string }) {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6 } as const;

  return (
    <span
      aria-hidden
      className={cn(
        'grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-bone-raised',
        outcome === 'running' && 'animate-ledger-pulse',
        className,
      )}
      title={OUTCOME_LABEL[outcome]}
    >
      <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" {...stroke}>
        {outcome === 'succeeded' ? <path d="M2 6.4 4.6 9 10 3.2" /> : null}
        {outcome === 'running' ? (
          <>
            <circle cx="6" cy="6" r="4.6" />
            <path d="M6 6V3.6" />
          </>
        ) : null}
        {outcome === 'failed' ? (
          <>
            <circle cx="6" cy="6" r="4.6" />
            <path d="M4.2 7.8 7.8 4.2" />
          </>
        ) : null}
        {outcome === 'expired' ? (
          <>
            <circle cx="6" cy="6" r="4.6" />
            <path d="M6 3.4V6l1.8 1.4" />
          </>
        ) : null}
        {outcome === 'cancelled' ? <path d="M2.6 6h6.8" /> : null}
      </svg>
    </span>
  );
}
